import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import { gql, useQuery } from '@apollo/client';
import { NextSeo } from 'next-seo';

import config from 'config';
import { formatCloudinaryUrl } from 'lib/formatters';
import { serializeToText } from 'lib/serializeTiptap';
import useLastValid from 'hooks/useLastValid';
import { useCampaignContribute } from 'context/CampaignContribute';

const GET_CAMPAIGN_CONTRIBUTE_SEO_PROPERTIES = gql`
  query GetCampaignContributeSeoProperties(
    $campaignId: String!
    $fundraiserId: String
    $teamId: String
    $includeFundraiser: Boolean!
    $includeTeam: Boolean!
  ) {
    findCampaigns(id: $campaignId) {
      id
      fundraiserDefaultHeaderBackgroundImage
      fundraiserDefaultStory
      name
      teamDefaultHeaderBackgroundImage
      teamDefaultStory
    }

    findFundraisers(id: $fundraiserId) @include(if: $includeFundraiser) {
      id
      headerImage
      resolvedName
      story
    }

    findTeams(id: $teamId) @include(if: $includeTeam) {
      id
      headerImage
      name
      story
    }
  }
`;

const CampaignContributeSeo = ({ pageTitleDetail }) => {
  const router = useRouter();
  const { campaignId, contribution } = useCampaignContribute();

  const { data } = useQuery(GET_CAMPAIGN_CONTRIBUTE_SEO_PROPERTIES, {
    variables: {
      campaignId,
      fundraiserId: contribution.fundraiserId,
      teamId: contribution.teamId,
      includeFundraiser: !!contribution.fundraiserId,
      includeTeam: !!contribution.teamId,
    },
  });

  const campaign = useLastValid(data?.findCampaigns[0]);
  const fundraiser = useMemo(() => data?.findFundraisers?.[0], [data]);
  const team = useMemo(() => data?.findTeams?.[0], [data]);

  const titleDetail = pageTitleDetail ? ` - ${pageTitleDetail}` : '';

  const title = useMemo(() => {
    if (fundraiser) return `${fundraiser.resolvedName} - ${campaign.name}${titleDetail}`;
    if (team) return `${team.name} - ${campaign.name}${titleDetail}`;
    return `${campaign?.name}${titleDetail}` ?? null;
  }, [campaign, team, fundraiser, titleDetail]);

  const description = useMemo(() => {
    if (fundraiser) {
      return serializeToText(fundraiser.story ?? campaign.fundraiserDefaultStory);
    }

    if (team) {
      return serializeToText(team.story ?? campaign.teamDefaultStory);
    }

    return campaign?.campaignHeadline ?? null;
  }, [campaign, team, fundraiser]);

  const image = useMemo(() => {
    if (fundraiser) {
      return (
        fundraiser.headerImage ??
        campaign.fundraiserDefaultHeaderBackgroundImage ??
        config('/defaultFundraiserHeaderImage')
      );
    }

    if (team) {
      return (
        team.headerImage ??
        campaign.teamDefaultHeaderBackgroundImage ??
        config('/defaultTeamHeaderImage')
      );
    }

    return campaign?.campaignHeaderBackgroundImage ?? config('/defaultCampaignHero');
  }, [fundraiser, team, campaign]);

  return (
    <NextSeo
      title={`Support ${title}`}
      description={description}
      openGraph={{
        // Duplicated here so it doesn't get the site name appended
        title: `Support ${title}`,
        url: `${config('/hostname')}${router.asPath}`,
        images: [
          {
            url: formatCloudinaryUrl(image, { transformation: 'facebook' }),
            width: 1200,
            height: 630,
          },
        ],
      }}
    />
  );
};

CampaignContributeSeo.propTypes = {
  pageTitleDetail: PropTypes.string,
};

CampaignContributeSeo.defaultProps = {
  pageTitleDetail: null,
};

export default CampaignContributeSeo;
